import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, Calendar, Sparkles, Loader2, X, CheckSquare, Square } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";

function AIGenerateModal({ open, onClose, onGenerated }) {
    const [form, setForm] = useState({ goal: "hypertrophy", weeks: 4, days_per_week: 4, experience: "intermediate", equipment: "Full gym", emphasis: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    if (!open) return null;
    const submit = async (e) => {
        e.preventDefault(); setBusy(true); setErr("");
        try {
            const { data } = await api.post("/ai/generate-program", form);
            onGenerated(data); onClose();
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="ai-gen-modal">
            <div className="bg-[var(--surface)] border border-[var(--gold-deep)] w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
                    <div className="flex items-center gap-2">
                        <Sparkles size={18} className="text-[var(--gold-bright)]" />
                        <h3 className="font-serif text-2xl">AI program generator</h3>
                    </div>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="label-tiny block mb-2">Goal</label>
                            <select className="input-dark" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} data-testid="ai-goal">
                                <option value="hypertrophy">Hypertrophy</option>
                                <option value="strength">Strength</option>
                                <option value="endurance">Endurance</option>
                                <option value="fat_loss">Fat loss</option>
                                <option value="rehab">Rehab</option>
                                <option value="general">General fitness</option>
                            </select>
                        </div>
                        <div>
                            <label className="label-tiny block mb-2">Experience</label>
                            <select className="input-dark" value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })}>
                                <option value="beginner">Beginner</option>
                                <option value="intermediate">Intermediate</option>
                                <option value="advanced">Advanced</option>
                            </select>
                        </div>
                        <div>
                            <label className="label-tiny block mb-2">Weeks</label>
                            <input type="number" min={1} max={16} className="input-dark" value={form.weeks} onChange={(e) => setForm({ ...form, weeks: parseInt(e.target.value) || 1 })} />
                        </div>
                        <div>
                            <label className="label-tiny block mb-2">Days / week</label>
                            <input type="number" min={1} max={7} className="input-dark" value={form.days_per_week} onChange={(e) => setForm({ ...form, days_per_week: parseInt(e.target.value) || 1 })} />
                        </div>
                    </div>
                    <div>
                        <label className="label-tiny block mb-2">Equipment</label>
                        <input className="input-dark" value={form.equipment} onChange={(e) => setForm({ ...form, equipment: e.target.value })} />
                    </div>
                    <div>
                        <label className="label-tiny block mb-2">Extra emphasis (optional)</label>
                        <textarea className="input-dark min-h-[70px]" placeholder="e.g. focus posterior chain, include conditioning on off days…" value={form.emphasis} onChange={(e) => setForm({ ...form, emphasis: e.target.value })} />
                    </div>
                    {err && <div className="text-sm text-[var(--danger)]" data-testid="ai-gen-error">{err}</div>}
                    <div className="flex justify-between items-center pt-2">
                        <p className="text-[10px] text-[var(--text-faint)] font-mono">Claude Sonnet 4.5 · ~10-15s</p>
                        <div className="flex gap-3">
                            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                            <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={busy} data-testid="ai-gen-submit">
                                {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                                {busy ? "Generating…" : "Generate"}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function ProgramsPage() {
    const [programs, setPrograms] = useState([]);
    const [aiOpen, setAiOpen] = useState(false);
    const [selectMode, setSelectMode] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const nav = useNavigate();
    const confirm = useConfirm();

    const load = async () => {
        const { data } = await api.get("/programs");
        setPrograms(data);
    };
    useEffect(() => { load(); }, []);

    const toggleOne = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelected(next);
    };
    const toggleAll = () => {
        if (selected.size === programs.length) setSelected(new Set());
        else setSelected(new Set(programs.map((p) => p.id)));
    };
    const cancelSelect = () => { setSelectMode(false); setSelected(new Set()); };

    const bulkDelete = async () => {
        if (selected.size === 0) return;
        const ok = await confirm({
            title: `Delete ${selected.size} program${selected.size > 1 ? "s" : ""}?`,
            message: "Already-scheduled workouts will remain on the calendar — only the templates are removed.",
            confirmLabel: `Delete ${selected.size}`,
        });
        if (!ok) return;
        await api.post("/programs/bulk-delete", { ids: Array.from(selected) });
        setPrograms(programs.filter((p) => !selected.has(p.id)));
        cancelSelect();
    };

    const remove = async (id, name) => {
        const ok = await confirm({ title: "Delete program?", message: `"${name || "This program"}" will be removed. Any workouts already scheduled from it remain intact.`, confirmLabel: "Delete program" });
        if (!ok) return;
        await api.delete(`/programs/${id}`);
        setPrograms(programs.filter((p) => p.id !== id));
    };

    const createNew = async () => {
        const { data } = await api.post("/programs", {
            name: "Untitled Program", description: "", weeks: 4, days_per_week: 4, goal: "general", days: [],
        });
        nav(`/programs/${data.id}`);
    };

    return (
        <div className="p-8 space-y-6" data-testid="programs-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="label-tiny mb-2">/ Templates</p>
                    <h1 className="font-serif text-6xl">Programs</h1>
                </div>
                <div className="flex gap-3 flex-wrap">
                    {selectMode ? (
                        <>
                            <button onClick={toggleAll} className="btn-ghost inline-flex items-center gap-2" data-testid="programs-select-all">
                                {selected.size === programs.length && programs.length > 0 ? <CheckSquare size={14} /> : <Square size={14} />}
                                {selected.size}/{programs.length}
                            </button>
                            <button onClick={bulkDelete} disabled={selected.size === 0} className="btn-primary bg-[var(--danger)] hover:bg-[var(--danger)] inline-flex items-center gap-2" data-testid="programs-bulk-delete">
                                <Trash2 size={14} /> Delete selected
                            </button>
                            <button onClick={cancelSelect} className="btn-ghost" data-testid="programs-cancel-select">Cancel</button>
                        </>
                    ) : (
                        <>
                            {programs.length > 0 && (
                                <button onClick={() => setSelectMode(true)} className="btn-ghost inline-flex items-center gap-2" data-testid="programs-select-mode">
                                    <CheckSquare size={14} /> Select
                                </button>
                            )}
                            <button onClick={() => setAiOpen(true)} className="btn-ghost inline-flex items-center gap-2" data-testid="ai-generate-btn">
                                <Sparkles size={14} className="text-[var(--gold-bright)]" /> AI generate
                            </button>
                            <button onClick={createNew} className="btn-primary inline-flex items-center gap-2" data-testid="new-program-btn">
                                <Plus size={14} /> New program
                            </button>
                        </>
                    )}
                </div>
            </div>

            {programs.length === 0 ? (
                <div className="border border-dashed border-[var(--border-bright)] bg-[var(--surface)] p-12 text-center">
                    <p className="font-serif text-3xl mb-2">No programs yet</p>
                    <p className="text-sm text-[var(--text-muted)] mb-5">Build a multi-week template by hand, or let Claude draft one in 10 seconds.</p>
                    <div className="flex gap-3 justify-center">
                        <button onClick={createNew} className="btn-primary">Create blank →</button>
                        <button onClick={() => setAiOpen(true)} className="btn-ghost inline-flex items-center gap-2"><Sparkles size={14} className="text-[var(--gold-bright)]" /> AI generate</button>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)]">
                    {programs.map((p) => (
                        <div key={p.id} className={`bg-[var(--surface)] p-6 transition-colors ${selectMode && selected.has(p.id) ? "ring-2 ring-[var(--gold)]" : "hover:bg-[var(--surface-hover)]"}`} data-testid={`program-card-${p.id}`}>
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-start gap-3 flex-1">
                                    {selectMode && (
                                        <button onClick={() => toggleOne(p.id)} className="mt-1 text-[var(--gold)]" data-testid={`program-select-${p.id}`}>
                                            {selected.has(p.id) ? <CheckSquare size={16} /> : <Square size={16} />}
                                        </button>
                                    )}
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <p className="label-tiny">{p.goal}</p>
                                            {p.generated_by_ai && <span className="tag-pill border-[var(--gold-deep)] text-[var(--gold-bright)] inline-flex items-center gap-1"><Sparkles size={10} /> AI</span>}
                                        </div>
                                        <Link to={`/programs/${p.id}`} className="font-serif text-2xl hover:text-[var(--gold)]">{p.name}</Link>
                                    </div>
                                </div>
                                {!selectMode && (
                                    <button onClick={() => remove(p.id, p.name)} className="text-[var(--text-muted)] hover:text-[var(--danger)]" data-testid={`program-delete-${p.id}`}>
                                        <Trash2 size={14} />
                                    </button>
                                )}
                            </div>
                            <div className="flex gap-4 font-mono text-xs text-[var(--text-muted)] mb-4">
                                <span>{p.weeks}w</span><span>·</span>
                                <span>{p.days_per_week}d/wk</span><span>·</span>
                                <span>{(p.days || []).length} days</span>
                            </div>
                            {p.description && <p className="text-sm text-[var(--text-muted)] line-clamp-2 mb-4">{p.description}</p>}
                            <div className="flex gap-2">
                                <Link to={`/programs/${p.id}`} className="btn-ghost text-xs flex-1 text-center">Edit</Link>
                                <Link to={`/programs/${p.id}?assign=1`} className="btn-primary text-xs inline-flex items-center gap-1.5">
                                    <Calendar size={12} /> Assign
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <AIGenerateModal open={aiOpen} onClose={() => setAiOpen(false)} onGenerated={(p) => { setPrograms([p, ...programs]); nav(`/programs/${p.id}`); }} />
        </div>
    );
}
